// A day's entries as cards (screens/CALENDAR.md, "What is on a day"): each
// says in words what it is — my players by name, the waiver run and my claims
// in it, the offer and whose — with its time, a word beside the time when one
// matters, and a tap to its screen. The calendar's cells draw them close; a
// phone's day and the one-week layout draw them roomy, each player on a line
// of his own with his position and opponent. Today's list has a line at now.

import { Link } from 'react-router-dom';
import type { CalendarPlayer, CalendarWeek } from '@fsp/domain';
import { PositionPill, StatusBadge } from '../../kit';
import { Icon } from '../../kit/Icon';
import { Text } from '../../kit/ui';
import { weekLine, clockIn, whenIn, type Entry } from './words';

/** The week's line on a phone's day: its name, whom I play, and my players on a bye. */
export function WeekLine({ week }: { week: CalendarWeek }) {
  return (
    <div className="fsp-cal-week">
      <Text size="sm" block>
        <strong>{weekLine(week)}</strong>
      </Text>
      {week.byes.length > 0 ? (
        <Text size="sm" tone="secondary" block>
          Bye: {week.byes.map((p) => p.shortName).join(', ')}
        </Text>
      ) : null}
    </div>
  );
}

const hurt = (p: CalendarPlayer) => p.health === 'out' || p.health === 'warn';

/**
 * MY PLAYERS AT A KICKOFF, BY NAME. Close, each starter on a line and the
 * bench together under them, quieter; roomy, each with his position, his
 * opponent and his points once he has played (Standard). One who cannot play
 * is in the warning color, with his status.
 */
function Players({
  players,
  roomy,
  numbers,
}: {
  players: readonly CalendarPlayer[];
  roomy: boolean;
  numbers: boolean;
}) {
  const starters = players.filter((p) => p.slotKind === 'starter');
  const bench = players.filter((p) => p.slotKind === 'bench');
  if (roomy) {
    const line = (p: CalendarPlayer) => (
      <span
        key={p.player.id}
        className="fsp-cal-card-player"
        data-bench={p.slotKind === 'bench' || undefined}
        data-hurt={hurt(p) || undefined}
      >
        <PositionPill pos={p.player.positions[0] ?? ''} size="sm" />
        <span className="fsp-cal-card-name">{p.player.shortName}</span>
        <StatusBadge player={p.player} />
        {p.opponent !== null ? <span className="fsp-cal-card-opp">{p.opponent}</span> : null}
        {numbers && p.points !== null ? (
          <span className="fsp-cal-card-points">{p.points.toFixed(1)}</span>
        ) : null}
      </span>
    );
    return (
      <span className="fsp-cal-card-players" data-roomy>
        {starters.map(line)}
        {bench.length > 0 ? <span className="fsp-cal-card-sub">Bench</span> : null}
        {bench.map(line)}
      </span>
    );
  }
  return (
    <span className="fsp-cal-card-players">
      {starters.map((p) => (
        <span key={p.player.id} className="fsp-cal-card-player" data-hurt={hurt(p) || undefined}>
          <span className="fsp-cal-card-name">{p.player.shortName}</span>
          <StatusBadge player={p.player} />
        </span>
      ))}
      {bench.length > 0 ? (
        <span className="fsp-cal-card-bench">
          Bench: {bench.map((p) => p.player.shortName).join(', ')}
        </span>
      ) : null}
    </span>
  );
}

export function EntryCard({
  entry,
  zone,
  base,
  numbers,
  roomy,
  past,
}: {
  entry: Entry;
  zone: string;
  base: string;
  numbers: boolean;
  roomy: boolean;
  past: boolean;
}) {
  const pre = entry.players.some((p) => p.state === 'pre');
  const lines = [
    ...entry.lines,
    ...(roomy && entry.early ? ['Before the week’s main games'] : []),
    ...(roomy && entry.locks && pre && entry.kind === 'games' ? ['Locks at kickoff'] : []),
  ];
  const body = (
    <>
      <span className="fsp-cal-card-head">
        <Icon name={entry.icon} size="xs" />
        {entry.timeless ? null : (
          <span className="fsp-cal-card-time">{clockIn(entry.at, zone)}</span>
        )}
        {entry.tag !== null ? <span className="fsp-cal-card-tag">{entry.tag}</span> : null}
      </span>
      {entry.card !== null ? <span className="fsp-cal-card-title">{entry.card}</span> : null}
      {entry.players.length > 0 ? (
        <Players players={entry.players} roomy={roomy} numbers={numbers} />
      ) : null}
      {lines.map((l, i) => (
        <span key={i} className="fsp-cal-card-line">
          {l}
        </span>
      ))}
    </>
  );
  const attrs = {
    className: 'fsp-cal-card',
    'data-tone': entry.tone,
    'data-needs': entry.needsMe || undefined,
    'data-past': past || undefined,
    'data-kind': entry.kind,
  };
  return (
    <li className="fsp-cal-item">
      {entry.to === null ? (
        <div {...attrs}>{body}</div>
      ) : (
        <Link to={`${base}${entry.to}`} title={entry.open ?? undefined} {...attrs}>
          {body}
        </Link>
      )}
    </li>
  );
}

/**
 * A day's cards in time order, today's with a line at now between what has
 * happened and what is to come.
 */
export function DayList({
  entries,
  now,
  today,
  zone,
  base,
  numbers,
  roomy,
  label,
  col,
}: {
  entries: readonly Entry[];
  now: number;
  /** The day is today, so it has a line at now. */
  today: boolean;
  zone: string;
  base: string;
  numbers: boolean;
  roomy: boolean;
  /** What a screen reader hears the list as: the day and its week. */
  label: string;
  /** The calendar's column, for its place in a row. */
  col?: number;
}) {
  const items = [];
  let marked = !today || entries.length === 0;
  for (const e of entries) {
    if (!marked && e.at >= now) {
      items.push(<NowLine key="now" />);
      marked = true;
    }
    items.push(
      <EntryCard
        key={e.key}
        entry={e}
        zone={zone}
        base={base}
        numbers={numbers}
        roomy={roomy}
        past={!e.timeless && e.at < now}
      />,
    );
  }
  if (!marked) items.push(<NowLine key="now" />);
  return (
    <ul className="fsp-cal-cards" aria-label={label} data-col={col}>
      {items}
    </ul>
  );
}

function NowLine() {
  return (
    <li className="fsp-cal-now" aria-hidden>
      <span>Now</span>
    </li>
  );
}

/**
 * A phone's day: its week, its cards, and with none what comes next, so an
 * empty day still points somewhere.
 */
export function DayDetail({
  dayName,
  label,
  week,
  entries,
  next,
  now,
  today,
  zone,
  base,
  numbers,
  loading,
}: {
  /** "Wednesday". */
  dayName: string;
  /** "Wednesday, Oct 29", for a screen reader. */
  label: string;
  week: CalendarWeek | null;
  entries: readonly Entry[];
  /** The first entry after this day, when there is one in what was read. */
  next: Entry | null;
  now: number;
  today: boolean;
  zone: string;
  base: string;
  numbers: boolean;
  loading: boolean;
}) {
  return (
    <div className="fsp-cal-detail">
      {week !== null ? <WeekLine week={week} /> : null}
      {entries.length > 0 ? (
        <DayList
          entries={entries}
          now={now}
          today={today}
          zone={zone}
          base={base}
          numbers={numbers}
          roomy
          label={label}
        />
      ) : loading ? null : (
        <div className="fsp-cal-nothing">
          <Text size="sm" block>
            Nothing on {dayName}.
          </Text>
          {next !== null ? (
            <Text size="sm" tone="secondary" block>
              Next: {whenIn(next.at, zone)}, {next.title.replace(/\.$/, '')}.
            </Text>
          ) : null}
        </div>
      )}
    </div>
  );
}
